import { useState } from "react";
import { motion } from "framer-motion";
import { Camera, Upload, Check, AlertCircle, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import Layout from "@/components/layout/Layout";
import PageHero from "@/components/shared/PageHero";
import { AdBanner } from "@/components/shared/AdPlaceholder";
import axios from "axios";
import { API } from "@/App";

export default function CompartirPage() {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    author_name: "",
    author_email: "",
    year_approx: "",
    location: "",
    category: "general",
  });
  const [images, setImages] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + images.length > 5) {
      toast.error("Máximo 5 imágenes");
      return;
    }
    setImages([...images, ...files]);
    
    // Generate previews
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviews((prev) => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index) => {
    setImages(images.filter((_, i) => i !== index));
    setPreviews(previews.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.description || !formData.author_name || !formData.author_email) {
      toast.error("Por favor completa todos los campos requeridos");
      return;
    }

    setLoading(true);
    
    try {
      const data = new FormData();
      data.append("title", formData.title);
      data.append("description", formData.description);
      data.append("author_name", formData.author_name);
      data.append("author_email", formData.author_email);
      data.append("year_approx", formData.year_approx);
      data.append("location", formData.location);
      data.append("category", formData.category);
      
      images.forEach((image) => {
        data.append("images", image);
      });

      await axios.post(`${API}/memories/submit`, data, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setSubmitted(true);
      toast.success("¡Gracias por compartir tu memoria!");
    } catch (error) {
      toast.error("Error al enviar. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <Layout>
        <div className="min-h-[70vh] flex items-center justify-center px-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-md text-center"
          >
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
              <Check className="h-10 w-10 text-green-600" />
            </div>
            <h1 className="font-heading text-3xl font-bold text-brand-text mb-4">
              ¡Gracias por compartir!
            </h1>
            <p className="text-brand-textSecondary mb-8">
              Tu memoria ha sido enviada y será revisada por nuestro equipo. Te notificaremos cuando sea publicada.
            </p>
            <Button onClick={() => setSubmitted(false)} className="rounded-full">
              Enviar otra memoria
            </Button>
          </motion.div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Hero */}
      <PageHero
        section="compartir"
        title="Comparte tu Memoria"
        subtitle="¿Tienes fotos antiguas, historias familiares o recuerdos de Choluteca? Ayúdanos a preservar la memoria colectiva de nuestra comunidad."
        badge="Contribuye"
        badgeIcon={Share2}
      />

      {/* Form */}
      <section className="py-12 md:py-16" data-testid="submit-form-section">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Title */}
            <div>
              <Label htmlFor="title">Título de tu memoria *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Ej: El mercado de Choluteca en los años 80"
                className="mt-1"
                required
                data-testid="memory-title-input"
              />
            </div>

            {/* Description */}
            <div>
              <Label htmlFor="description">Cuéntanos la historia *</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe qué representa esta memoria, cuándo fue, quiénes aparecen, qué significado tiene para ti..."
                rows={6}
                className="mt-1"
                required
                data-testid="memory-description-input"
              />
            </div>

            {/* Category */}
            <div>
              <Label>Tipo de memoria</Label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData({ ...formData, category: value })}
              >
                <SelectTrigger className="mt-1" data-testid="memory-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">Historia general</SelectItem>
                  <SelectItem value="antes-despues">Antes / Después (comparativa)</SelectItem>
                  <SelectItem value="tradicion">Tradición o costumbre</SelectItem>
                  <SelectItem value="personaje">Personaje memorable</SelectItem>
                  <SelectItem value="oficio">Oficio o profesión antigua</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Year */}
              <div>
                <Label htmlFor="year_approx">Año aproximado</Label>
                <Input
                  id="year_approx"
                  value={formData.year_approx}
                  onChange={(e) => setFormData({ ...formData, year_approx: e.target.value })}
                  placeholder="Ej: 1985 o Década de los 70"
                  className="mt-1"
                />
              </div>

              {/* Location */}
              <div>
                <Label htmlFor="location">Lugar</Label>
                <Input
                  id="location"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="Ej: Centro de Choluteca"
                  className="mt-1"
                />
              </div>
            </div>

            {/* Images */}
            <div>
              <Label>Fotos (máximo 5)</Label>
              <div className="mt-2">
                {previews.length > 0 ? (
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    {previews.map((preview, idx) => (
                      <div key={idx} className="relative aspect-square">
                        <img
                          src={preview}
                          alt={`Preview ${idx + 1}`}
                          className="w-full h-full object-cover rounded-xl"
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(idx)}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full text-sm"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                ) : null}
                
                {images.length < 5 && (
                  <label className="block">
                    <div className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center cursor-pointer hover:border-primary transition-colors">
                      <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-brand-textSecondary">
                        Haz clic o arrastra tus fotos aquí
                      </p>
                      <p className="text-xs text-brand-textMuted mt-1">
                        JPG, PNG (máx. 10MB cada una)
                      </p>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageChange}
                      className="hidden"
                      data-testid="image-upload-input"
                    />
                  </label>
                )}
              </div>
            </div>

            <hr className="border-gray-200" />

            {/* Author Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="author_name">Tu nombre *</Label>
                <Input
                  id="author_name"
                  value={formData.author_name}
                  onChange={(e) => setFormData({ ...formData, author_name: e.target.value })}
                  placeholder="Tu nombre completo"
                  className="mt-1"
                  required
                  data-testid="author-name-input"
                />
              </div>
              <div>
                <Label htmlFor="author_email">Tu email *</Label>
                <Input
                  id="author_email"
                  type="email"
                  value={formData.author_email}
                  onChange={(e) => setFormData({ ...formData, author_email: e.target.value })}
                  placeholder="tu@email.com"
                  className="mt-1"
                  required
                  data-testid="author-email-input"
                />
              </div>
            </div>

            {/* Info */}
            <div className="bg-blue-50 rounded-xl p-4 flex gap-3">
              <AlertCircle className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium">Antes de enviar:</p>
                <ul className="list-disc list-inside mt-1 text-blue-700">
                  <li>Tu memoria será revisada antes de publicarse</li>
                  <li>Solo usaremos tu email para notificarte</li>
                  <li>Puedes pedir que se retire en cualquier momento</li>
                </ul>
              </div>
            </div>

            {/* Submit */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full rounded-full py-6 text-lg"
              data-testid="submit-memory-btn"
            >
              {loading ? (
                "Enviando..."
              ) : (
                <>
                  <Camera className="mr-2 h-5 w-5" />
                  Enviar mi memoria
                </>
              )}
            </Button>
          </form>
        </div>
      </section>

      {/* Ad Banner */}
      <section className="py-8 bg-gray-50">
        <AdBanner />
      </section>
    </Layout>
  );
}
